import { dateFormat } from '@astronautsid/wpe-utils'
import {
  DEFAULT_PRODUCT_IMAGE_URL,
  getFilePath,
} from '../pages/ProductAdd/components/ProductUploadImage'
import { ProductFormSchemaType } from '../schema/product'
import { GetProductByIdDataInterface } from '../@types/ProductMasterDetail'
import { ProductAddEditPayloadType, PutUpdateProductEditPayloadType } from '../@types/ProductAdd'

export type GeneralDataPayloadType = Pick<
  ProductAddEditPayloadType,
  | 'product_name'
  | 'product_sku_no'
  | 'product_description'
  | 'type_id'
  | 'product_categories'
  | 'bom_category'
  | 'brand_id'
  | 'product_label_image_id'
  | 'product_label_promotion_id'
  | 'product_image_paths'
> & {
  attributes: Pick<
    ProductAddEditPayloadType['attributes'],
    'business_unit' | 'business_taggings' | 'company' | 'source_status' | 'image_link'
  >
}

export type RegulationDataPayloadType = Pick<ProductAddEditPayloadType, 'is_allow_het'> & {
  attributes: Pick<
    ProductAddEditPayloadType['attributes'],
    | 'bpom'
    | 'sni'
    | 'pirt'
    | 'mui'
    | 'harga_eceran_terendah'
    | 'harga_eceran_tertinggi'
    | 'het_docs_agreement'
  >
}

export type SalesDataPayloadType = Pick<
  ProductAddEditPayloadType,
  'is_consignment' | 'is_food' | 'ppn_key' | 'is_seasonal_sku' | 'is_exclusive_launch'
> & {
  attributes: Pick<
    ProductAddEditPayloadType['attributes'],
    | 'freebies_period_start_date'
    | 'freebies_period_end_date'
    | 'seasonal_sku_period_start_date'
    | 'seasonal_sku_period_end_date'
    | 'exclusive_launch_period_start_date'
    | 'exclusive_launch_period_end_date'
    | 'cogs'
  >
}

const getCategoryUniqueIdAndPrimary = (
  data: ProductFormSchemaType['generalData']['product_categories'],
): GeneralDataPayloadType['product_categories'] => {
  const map = new Map()

  data.forEach((item) => {
    const { selectedCategory, isPrimaryCategory } = item

    selectedCategory.forEach((cat, index) => {
      const isLeaf = index === selectedCategory.length - 1

      if (!map.has(cat.id)) {
        map.set(cat.id, {
          id: cat.id,
          is_primary: isLeaf ? isPrimaryCategory : false,
        })
      } else if (isLeaf && isPrimaryCategory) {
        map.get(cat.id).is_primary = true
      }
    })
  })

  return Array.from(map.values())
}

export const composeGeneralDataPayload = (
  data: ProductFormSchemaType['generalData'],
): GeneralDataPayloadType => ({
  product_name: data.product_name,
  product_sku_no: data.product_sku_no,
  product_description: data.description || '',
  type_id: `${data.productType?.id || 0}`,
  bom_category: data.bomCategory?.key || '',
  brand_id: `${data.brand?.brand_id || 0}`,
  product_label_image_id: data.labelImage?.id || 0,
  product_label_promotion_id: data.labelPromotion?.id || 0,
  product_image_paths: [
    {
      path_large: getFilePath(DEFAULT_PRODUCT_IMAGE_URL.url_large),
      path_medium: getFilePath(DEFAULT_PRODUCT_IMAGE_URL.url_medium),
      path_small: getFilePath(DEFAULT_PRODUCT_IMAGE_URL.url_small),
      sequence: 1,
    },
  ],
  attributes: {
    business_unit: data.businessUnit,
    business_taggings: data.businessTaggings,
    company: data.company,
    source_status: data.statusSource || {
      key: '',
      label: '',
    },
    image_link: data.product_image_link || '',
  },
  product_categories: getCategoryUniqueIdAndPrimary(data.product_categories),
})

export const composeRegulationDataPayload = (
  data: ProductFormSchemaType['regulationData'],
): RegulationDataPayloadType => ({
  is_allow_het: data.HET.isHET,
  attributes: {
    bpom: data.legality.bpom,
    sni: data.legality.sni,
    pirt: data.legality.pirt,
    mui: data.legality.mui,
    het_docs_agreement: data.HET.HETDocs,
    harga_eceran_tertinggi: data.HET.HETHighest,
    harga_eceran_terendah: data.HET.HETLowest,
  },
})

const convertDatePayload = (date: Date | null): string => {
  if (!date) return ''

  return dateFormat({
    date: new Date(date),
    format: 'YYYY-MM-DD',
  })
}

export const composeSalesDataPayload = (
  data: ProductFormSchemaType['salesData'],
): SalesDataPayloadType => ({
  is_consignment: data.promotion.consignmentStatus,
  is_food: data.promotion.foodNonFood === 'food',
  ppn_key: Number.isNaN(+data.taxAndPricing.ppn) ? 0 : +data.taxAndPricing.ppn,
  is_seasonal_sku: data.promotion.isSeasonalSKU ?? false,
  is_exclusive_launch: data.promotion.isExclusiveLaunch ?? false,
  attributes: {
    freebies_period_start_date: convertDatePayload(data.promotion.freebiesStartDate),
    freebies_period_end_date: convertDatePayload(data.promotion.freebiesEndDate),
    seasonal_sku_period_start_date: convertDatePayload(data.promotion.seasonalSKUStartDate),
    seasonal_sku_period_end_date: convertDatePayload(data.promotion.seasonalSKUEndDate),
    exclusive_launch_period_start_date: convertDatePayload(data.promotion.exclusiveLaunchStartDate),
    exclusive_launch_period_end_date: convertDatePayload(data.promotion.exclusiveLaunchEndDate),
    cogs: data.taxAndPricing.cogs,
  },
})

/**
 * Reshapes GetProductByIdDataInterface data into PutUpdateProductStatusPayloadType
 * This function transforms the product detail data structure for update operations
 */
export const reshapeProductDetailToUpdatePayload = (
  data: GetProductByIdDataInterface,
): PutUpdateProductEditPayloadType => ({
  // Basic product information
  product_sku_no: data.product_sku_no,
  product_name: data.product_name,
  base_uom: data.base_uom.id,
  product_description: data.product_description,
  weight: data.weight,
  length: data.length,
  width: data.width,
  height: data.height,

  // Required fields that were missing
  type_id: data.type.id.toString(),
  brand_id: data.brand.id.toString(),
  ppn_key: Number(data.ppn.key),
  product_image_paths: data.product_image_paths.map((img, index) => ({
    path_large: img.path_large,
    path_medium: img.path_medium,
    path_small: img.path_small,
    sequence: img.sequence || index + 1,
    path_real: img.path_real,
    blur_hash: img.blur_hash,
  })),

  // Product configuration
  bom_category: data.bom_category,
  is_consignment: data.is_consignment,
  relabeled_in_hub: data.relabeled_in_hub,
  relabeled_in_wh: data.relabeled_in_wh,
  use_standard_ned: data.use_standard_ned,

  // Inventory settings
  mslod: data.mslod,
  mslor: data.mslor,
  msltc: data.msltc,
  max_inventory_balance: data.max_inventory_balance,
  max_days_of_inventory: data.max_days_of_inventory,
  min_days_of_inventory: data.min_days_of_inventory,

  // Labels (optional, defaulting to 0 if not present)
  product_label_promotion_id: data.product_label_promotion_id || 0,
  product_label_image_id: data.product_label_image_id || 0,

  // Categories
  product_categories: data.product_category_ids.map((cat) => ({
    id: cat.id,
    is_primary: cat.is_primary,
  })),

  // Attributes - transforming the nested object structure
  attributes: {
    source_status: data.attributes.source_status,
    produced_by: data.attributes.produced_by,
    product_age: data.attributes.product_age,
    flush_out_age: data.attributes.flush_out_age,
    recycling_age: data.attributes.recycling_age,
    expiration_reminder: data.attributes.expiration_reminder,
    max_qty: data.attributes.max_qty,
    max_qty_duration: data.attributes.max_qty_duration,

    // Transform business entities
    business_unit: data.attributes.business_unit
      ? {
          id: data.attributes.business_unit.id,
          key: data.attributes.business_unit.key,
          type: data.attributes.business_unit.type,
          value: data.attributes.business_unit.value,
        }
      : null,

    company: data.attributes.company
      ? {
          id: data.attributes.company.id,
          key: data.attributes.company.key,
          type: data.attributes.company.type,
          value: data.attributes.company.value,
        }
      : null,

    business_taggings: data.attributes.business_taggings.map((tag) => ({
      id: tag.id.toString(),
      key: tag.key,
      type: tag.type,
      value: tag.value,
    })),

    // Regulatory information
    bpom: data.attributes.bpom,
    sni: data.attributes.sni,
    pirt: data.attributes.pirt,
    mui: data.attributes.mui,
    shelf: data.attributes.shelf,

    // Pricing
    cogs: data.attributes.cogs,
    harga_eceran_tertinggi: data.attributes.harga_eceran_tertinggi,
    harga_eceran_terendah: data.attributes.harga_eceran_terendah,
    het_docs_agreement: data.attributes.het_docs_agreement,

    // Date periods
    freebies_period_start_date: data.attributes.freebies_period_start_date,
    freebies_period_end_date: data.attributes.freebies_period_end_date,
    seasonal_sku_period_start_date: data.attributes.seasonal_sku_period_start_date,
    seasonal_sku_period_end_date: data.attributes.seasonal_sku_period_end_date,
    exclusive_launch_period_start_date: data.attributes.exclusive_launch_period_start_date,
    exclusive_launch_period_end_date: data.attributes.exclusive_launch_period_end_date,

    // Asset IDs - converting number to string
    asset_id_storage_wh: data.attributes.asset_id_storage_wh.toString(),
    asset_id_storage_hub: data.attributes.asset_id_storage_hub.toString(),
    asset_id_outbound: data.attributes.asset_id_outbound.toString(),

    // Physical properties
    palletization_detail: data.attributes.palletization_detail,
    max_stackability: data.attributes.max_stackability,
    total_qty_per_pallet: data.attributes.total_qty_per_pallet,
    max_qty_per_bin_at_wh: data.attributes.max_qty_per_bin_at_wh,
    max_qty_per_bin_at_hub: data.attributes.max_qty_per_bin_at_hub,
    max_lead_time_outside_temperature: data.attributes.max_lead_time_outside_temperature,

    // Product placement and packaging
    product_placement: data.attributes.product_placement,
    packaging_type: data.attributes.packaging_type,
    packaging_thickness: data.attributes.packaging_thickness,

    // UOM information
    vendor_uom: data.attributes.vendor_uom,
    receiving_uom: data.attributes.receiving_uom,
    picking_uom: data.attributes.picking_uom,

    // Instructions
    instruction_receiving: data.attributes.instruction_receiving,
    instruction_picking: data.attributes.instruction_picking,

    // Miscellaneous
    image_link: data.attributes.image_link,
  },

  // Movement and storage handling
  sku_movement_handling: data.sku_movement_handling,
  storage_handling_id: data.storage_handling.id,

  // Feature flags
  is_food: data.is_food,
  is_seasonal_sku: data.is_seasonal_sku,
  is_exclusive_launch: data.is_exclusive_launch,
  is_high_value: data.is_high_value,
  is_high_risk: data.is_high_risk,
  is_halal: data.is_halal,
  is_substitute: data.is_substitute,
  is_allow_rfid: data.is_allow_rfid,
  is_bundling: data.is_bundling,
  is_qr: data.is_qr,
  is_allow_mix_sku: data.is_allow_mix_sku,
  is_allow_het: data.is_allow_het,
  is_critical_status: data.is_critical_status,
  is_auto_active_super: data.is_auto_active_super,
  is_single_packaging: data.is_single_packaging,
  is_special_handling: data.is_special_handling,
  is_eggs: data.is_eggs,
  is_melting: data.is_melting,
  is_rotten: data.is_rotten,
  is_fragile: data.is_fragile,
  is_sharp: data.is_sharp,
  is_refill: data.is_refill,
  is_broken_receipt: data.is_broken_receipt,
  is_leaking: data.is_leaking,
  is_slipped: data.is_slipped,
  is_flammable: data.is_flammable,

  // Status - this is the additional field in PutUpdateProductStatusPayloadType
  active: data.active,
})

export const getBuyerLandingProductPageURL = (slug: string) => {
  const baseURL = process.env.REACT_APP_LANDING_PRODUCT_PAGE_URL || ''

  return baseURL ? `${baseURL}/${slug}` : slug
}
